// Copyright 2024 Sorama B.V.

import { autoinject, Disposable } from "aurelia-framework";
import { Subscribable } from "libraries/Subscribable/Subscribable";
import { ColorAssigningStrategy } from "./ColorAssigningStrategy";
import { NamedColor } from "./NamedColor";

/** Handles changes to measurement colors with subscribers */
@autoinject
export class MeasurementColorService {
    private readonly colorPalette: NamedColor[];
    private readonly subscribable: Subscribable<NamedColor> = new Subscribable<NamedColor>();

    /** Should not be used directly, but to bind from HTML with Aurelia this is the only way afaik */
    public readonly assignedColors: { [id: string]: NamedColor } = {};

    constructor(colorPalette: NamedColor[]) {
        this.colorPalette = colorPalette;
    }

    public ensureAssignedColor(id: { measurementId: string; subIndex?: string }, newColor?: NamedColor): NamedColor {
        const combinedId = MeasurementColorService.combineIds(id.measurementId, id.subIndex);

        const nothingToAssign = !newColor && !!this.assignedColors[combinedId];
        const newColorIsSameAsCurrent =
            newColor &&
            !!this.assignedColors[combinedId] &&
            newColor.color.equals(this.assignedColors[combinedId].color);
        if (nothingToAssign || newColorIsSameAsCurrent) {
            // Nothing changed, return the current color without notifying
            return this.assignedColors[combinedId];
        }

        // If a color is given as an argument, just use that color
        if (newColor) {
            this.assignedColors[combinedId] = newColor;
        } else if (id.subIndex) {
            // If parent measurement color exists and this is the first subIndex that is assigned a color, use the measurement color
            const parentColor = this.assignedColors[id.measurementId];
            if (parentColor && this.countSubIndexes(id.measurementId) === 0) {
                newColor = parentColor;
                this.assignedColors[combinedId] = newColor;
            }
        }

        // If no color is determined, assign the next best color
        if (!newColor) {
            this.assignedColors[combinedId] = ColorAssigningStrategy.nextLeastOccuringColor(
                this.colorPalette,
                this.assignedColors
            );
        }

        this.subscribable.notify(combinedId, newColor);

        return this.assignedColors[combinedId];
    }

    public subscribe(
        id: { measurementId: string; subIndex?: string },
        callback: (value: NamedColor) => void
    ): Disposable {
        return this.subscribable.subscribe(MeasurementColorService.combineIds(id.measurementId, id.subIndex), callback);
    }

    public getColor(id: { measurementId: string; subIndex?: string }): NamedColor {
        return this.assignedColors[MeasurementColorService.combineIds(id.measurementId, id.subIndex)];
    }

    /**
     * Counts the number of subIndexes that are assigned a color for a measurement, not including the measurement itself.
     */
    private countSubIndexes(measurementId: string): number {
        // Get all assigned colors that start with the measurementId (but are not the measurementId itself)
        const assignedColors = Object.keys(this.assignedColors).filter(
            (key) => key.startsWith(measurementId) && key !== measurementId
        );
        return assignedColors.length;
    }

    public static combineIds(measurementId: string, subIndex?: string): string {
        return measurementId + (subIndex ? subIndex : "");
    }
}
